import { Alert, Badge, Box, Button, Grid, SpaceBetween, Table } from '@amzn/awsui-components-core';
import { ContentAccessStatus, ContentAccessUser } from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import UserIdAutosuggest, { AdditionlUserInfo } from './UserIdAutosuggest';

const initialInput = {
  alertMessage: null,
  newUserId: null,
  newUserName: null,
  newUserProfileName: null,
  newUserType: null,
  showAlert: false,
  validUserId: false,
};

interface Props {
  accessUsers?: ContentAccessUser[];
  formId: string;
  ownerId?: string;
  contentId: string;
  contentName?: string;
  onAccessUpdate?: (userAccess: ContentAccessUser[]) => void;
  max?: number;
  hideReviewersTable?: boolean;
  isAddDisabled?: boolean;
  isDeleteDisabled?: boolean;
}

interface State {
  newUserId?: string;
  newUserName?: string;
  /** Plain display name from the search result, without the type suffix shown in the dropdown label. */
  newUserProfileName?: string;
  newUserType?: string;
  validUserId?: boolean;
  accessUsers: ContentAccessUser[];
  showAlert?: boolean;
  alertMessage?: string;
}

class UserAccessPermissionForm extends React.Component<Props, State> {
  private mplServiceClient: IMplServiceClient;
  private columnDefinitions;

  constructor(props: Props) {
    super(props);
    this.mplServiceClient = MplServiceClientFactory.create();
    this.columnDefinitions = [
      {
        cell: (i: ContentAccessUser) => this.renderStatusBadge(i.status),
        header: 'Status',
        id: 'status',
      },
      {
        cell: (i: ContentAccessUser) => i.userName,
        header: 'Name',
        id: 'userName',
      },
      {
        cell: (i: ContentAccessUser) => i.userType,
        header: 'Type',
        id: 'type',
      },
    ];

    if (!this.props.isDeleteDisabled) {
      this.columnDefinitions.push({
        cell: (i: ContentAccessUser) => (
          <Button
            iconName="close"
            variant="icon"
            ariaLabel="Remove user"
            onClick={this.deleteUser.bind(this, i.userId)}
          />
        ),
        header: 'Remove',
        id: 'remove',
      });
    }

    // @ts-expect-error TS2322 - strict migration
    this.state = {
      ...initialInput,
      accessUsers: this.props.accessUsers || [],
    };
  }

  public async componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      contentId: prevPatternId,
      formId: prevFormId,
      accessUsers: prevAccessUsers,
    } = prevProps;
    const { contentId, formId, accessUsers } = this.props;
    if (contentId !== prevPatternId || formId !== prevFormId || prevAccessUsers !== accessUsers) {
      this.setState({
        // @ts-expect-error TS2322 - strict migration
        accessUsers,
      });
    }
  }

  public render() {
    const { hideReviewersTable, isAddDisabled } = this.props;
    const { accessUsers, showAlert, alertMessage, newUserId, newUserName, validUserId } =
      this.state;
    return (
      <SpaceBetween size="s">
        {showAlert && <Alert type="warning">{alertMessage}</Alert>}
        {!hideReviewersTable && (
          <Table
            columnDefinitions={this.columnDefinitions}
            empty={
              <Box padding={{ top: 's' }} margin={{ bottom: 'xs' }} fontWeight="bold">
                No users
              </Box>
            }
            items={accessUsers}
            wrapLines
            resizableColumns
          />
        )}
        <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
          <UserIdAutosuggest
            userId={newUserId}
            userName={newUserName}
            onUserSelected={this.updateNewUser}
          />
          <Button onClick={this.addNewUser} disabled={!validUserId || isAddDisabled}>
            Add
          </Button>
        </Grid>
      </SpaceBetween>
    );
  }

  private updateNewUser = async (
    userId: string,
    userName: string,
    validationError: any,
    additionalInfo?: AdditionlUserInfo,
  ) => {
    this.setState({
      newUserId: userId,
      newUserName: userName,
      newUserProfileName: additionalInfo?.name,
      newUserType: additionalInfo?.type,
      validUserId: !validationError,
    });
  };

  private addNewUser = async () => {
    const { ownerId, max, contentId } = this.props;
    const { newUserId, newUserName, newUserProfileName, newUserType, accessUsers } = this.state;

    // The Add button is only enabled after a valid selection, so a missing ID means nothing to add.
    if (!newUserId) return;

    let error;

    const [{ accessUsers: coAuthors }, { accessUsers: peerReviewers }] = await Promise.all([
      this.mplServiceClient.listContentSharedWith(contentId),
      this.mplServiceClient.listContentReviewers(contentId),
    ]);

    // @ts-expect-error TS18048 - strict migration
    if (accessUsers.length > max - 1) {
      error = `Maximum of ${max} users can be added.`;
    }

    if (newUserId === ownerId) {
      error = `${newUserName} is already the content owner. No additional access should be granted to its owner`;
    }

    if (coAuthors.some((ca) => ca.userId === newUserId)) {
      error = `${newUserName} is already the content co-author. No additional access should be granted to its co-author`;
    }

    if (peerReviewers.some((pr) => pr.userId === newUserId)) {
      error = `${newUserName} is already the content reviewer. No additional access should be granted to its reviewer`;
    }

    if (accessUsers.some((us) => us.userId === newUserId)) {
      error = `${newUserName} has already had access to the content `;
    }

    if (error) {
      this.raiseAlert(error);
      return;
    }

    // The pending row is display-only: saving sends just the user ID and the backend resolves the profile.
    const newAccess = ContentAccessUser.create(
      newUserId,
      newUserProfileName ?? newUserName ?? newUserId,
      newUserType ?? '',
      ContentAccessStatus.PENDING_ADD,
    );

    const newAccessUsers = accessUsers.concat(newAccess);
    this.updateAccess(newAccessUsers);
    this.clearInput();
  };

  private deleteUser = async (userId: string) => {
    let { accessUsers: newAccessUsers } = this.state;
    const deleteUser = newAccessUsers.find((u) => u.userId === userId);
    // @ts-expect-error TS18048 - strict migration
    if (deleteUser.status === ContentAccessStatus.PENDING_ADD) {
      newAccessUsers = newAccessUsers.filter((us) => us.userId !== userId);
    } else {
      // @ts-expect-error TS18048 - strict migration
      deleteUser.status = ContentAccessStatus.PENDING_DELETE;
    }
    this.updateAccess(newAccessUsers);
    this.clearInput();
  };

  // @ts-expect-error TS7006 - strict migration
  private renderStatusBadge(itemStatus) {
    switch (itemStatus) {
      case ContentAccessStatus.SAVED:
        return <Badge color="green" children="Saved" />;
      case ContentAccessStatus.PENDING_ADD:
        return <Badge color="blue" children="Pending Add" />;
      case ContentAccessStatus.PENDING_DELETE:
        return <Badge color="red" children="Pending Delete" />;
      default:
        return null;
    }
  }

  private updateAccess = (accessUsers: any[]) => {
    this.setState({
      accessUsers,
      // @ts-expect-error TS2322 - strict migration
      alertMessage: null,
      showAlert: false,
    });

    const { onAccessUpdate } = this.props;
    if (onAccessUpdate) {
      onAccessUpdate(accessUsers);
    }
  };

  private clearInput() {
    // @ts-expect-error TS2345 - strict migration
    this.setState({
      ...initialInput,
    });
  }

  private raiseAlert = (error: string) => {
    this.setState({
      alertMessage: error,
      showAlert: true,
    });
    return;
  };
}

export default UserAccessPermissionForm;
